import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState,} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

import {getErrorCode, parseError} from '../errors/http.service';
import {conversationService, CreateConversationRequestBody} from '../services/conversation.service';
import {StreamMessageParams} from '../services/streaming.service';
import {Message} from '../models/models';
import {
    createDefaultLoadingMessage,
    createDefaultUserMessage,
    extractCitations,
    extractThinking,
    findLastUserMessage
} from '../utils/chat-utils';
import {useUser} from './UserContext';
import {useSnackbar} from './SnackbarContext';
import {
    conversationReducer,
    ConversationState,
    initialState,
} from "./conversation/conversationReducer";
import {StreamingConversationManager} from "./conversation/StreamingConversationManager";
import {collectionService} from "../services/collection.service";
import {ERROR_CODES} from "../errors/errorCodes";

interface ConversationContextValue {
    state: ConversationState;
    sendMessage: (text: string, files: File[] | null, searchInternet: boolean) => Promise<void>;
    retryMessage: () => Promise<void>;
    retryInNewChat: () => Promise<void>;
    stopGeneration: () => void;
    updateSourcesFromUser: (groupIds: number[], docIds: number[]) => Promise<void>;
    updateCollectionsFromUser: (groupIds: number[]) => Promise<void>;
    updateDocumentsFromUser: (docIds: number[], wholeCollectionIds: number[]) => Promise<void>;
    loadConversationFromBackend: (id: string) => Promise<void>;
    createNewConversation: () => void;
    activateVariant: (variantId: number) => Promise<void>;
    exportToPdf: () => Promise<void>;
    setInitialModel: (modelId: number) => void;
    autoScrollFinished: boolean;
    setAutoScrollFinished: React.Dispatch<React.SetStateAction<boolean>>;
}

// --- Context ---

const ConversationContext = createContext<ConversationContextValue | null>(null);

const buildConversationSelectionPayload = (
    selectedGroupIds: number[],
    selectedDocIds: number[],
    selectedWholeCollectionIds: number[],
): CreateConversationRequestBody => ({
    collectionIds: selectedWholeCollectionIds.length > 0 ? selectedWholeCollectionIds : [],
    documentIds: selectedWholeCollectionIds.length > 0 ? [] : selectedDocIds,
});

// --- Provider ---

export const ConversationProvider = ({children}: {children: React.ReactNode}) => {
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { triggerConversationRefresh } = useUser();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const match = pathname.match(/^\/chat\/(\d+)$/);
    const urlConversationId = match ? match[1] : null;

    const [state, dispatch] = useReducer(conversationReducer, initialState);
    const [autoScrollFinished, setAutoScrollFinished] = useState(false);

    const streamManagerRef = useRef<StreamingConversationManager | null>(null);
    if (!streamManagerRef.current) {
        streamManagerRef.current = new StreamingConversationManager();
    }
    const streamManager = streamManagerRef.current;

    const streamingMessageRef = useRef<Message | null>(null);
    streamingMessageRef.current = state.streamingMessage;

    const conversationIdRef = useRef<string | null>(null);
    conversationIdRef.current = state.conversationId;

    // ref - blocks loading when data fetching is active
    const isLoadingFromBackendRef = useRef(false);

    const prevUrlConversationIdRef = useRef<string | null>(null);
    const isNavigatingFromSendRef = useRef(false);


    useEffect(() => {
        return () => {
            streamManager.stopAll();
        };
    }, []);

    const createNewConversation = useCallback(() => {
        dispatch({ type: 'CONVERSATION_RESET' });
    }, []);

    // TODO: conversationId: number
    const loadConversationFromBackend = useCallback(async (conversationId: string) => {
            isLoadingFromBackendRef.current = true;
            dispatch({ type: 'LOADING_STARTED' });

            try {
                const data = await conversationService.fetchConversation(conversationId);
                dispatch({ type: 'LOADING_FINISHED', payload: data });
            } catch (error) {
                showSnackbar(parseError(error), 'error');

                dispatch({ type: 'LOADING_FINISHED', payload: {
                        id: parseInt(conversationId, 10),
                        title: '',
                        messages: [],
                        answerVariants: [],
                        selectedGroupIds: [],
                        selectedDocIds: [],
                        selectedWholeCollectionIds: [],
                        selectedModelId: null,
                        searchInternet: false,
                        documents: [],
                    }});
            } finally {
                isLoadingFromBackendRef.current = false;
            }
        },
        [showSnackbar],
    );

    useEffect(() => {
        const prev = prevUrlConversationIdRef.current;
        const curr = urlConversationId;

        // conversation was changed while the stream was generating - stop active stream and save message
        if (prev !== null && prev !== curr) {
            const wasStreaming = streamManager.isStreaming(prev);
            streamManager.stopStream(prev);

            if (wasStreaming) {
                dispatch({ type: 'GENERATING_STOPPED' });

                const prevIdNum = parseInt(prev, 10);
                if (!isNaN(prevIdNum)) {
                    void conversationService.cancelGeneration(prev).catch(() => undefined);
                }
            }
        }

        if (curr && curr !== prev) {
            if (isNavigatingFromSendRef.current) {
                isNavigatingFromSendRef.current = false;
                prevUrlConversationIdRef.current = curr;

                return;
            }

            void loadConversationFromBackend(curr);
        }

        if (!curr && prev) {
            dispatch({ type: 'CONVERSATION_RESET' });
        }

        prevUrlConversationIdRef.current = curr;
    }, [urlConversationId, loadConversationFromBackend]);

    const ensureConversationExists = useCallback(async (): Promise<string> => {
        const currentId = conversationIdRef.current;

        if (currentId) {
            return currentId;
        }

        // attachments
        const requestBody = buildConversationSelectionPayload(
            state.selectedGroupIds ?? [],
            state.selectedDocIds ?? [],
            state.selectedWholeCollectionIds ?? [],
        );

        // requestBody - attachments, created conversationID
        const newId = await conversationService.createConversation(requestBody, false);

        return String(newId);
    }, [state.selectedDocIds, state.selectedGroupIds, state.selectedWholeCollectionIds]);


    const startStreamForConversation = useCallback(
        (params: StreamMessageParams, conversationId: string) => {
            streamManager.startStream(conversationId, params, {
                onChunk: (payload) => {
                    dispatch({
                        type: 'CHUNK_RECEIVED',
                        payload: {conversationId, ...payload},
                    });
                },

                onError: (err) => {
                    showSnackbar(parseError(err), 'error');
                    dispatch({
                        type: 'STREAM_ERROR',
                        payload: { contextExceeded: getErrorCode(err) === ERROR_CODES.CONTEXT_LIMIT_EXCEED},
                    });
                },

                onComplete: async (streamingText: string) => {
                    try {
                        const data = await conversationService.fetchConversation(conversationId);

                        const {cleanText: textWithThinking, citations} =
                            extractCitations(streamingText, data.documents);
                        const {cleanText, thinkingText} = extractThinking(textWithThinking);

                        const currentStreamingMsg = streamingMessageRef.current;
                        if (!currentStreamingMsg) return;

                        const lastIndex = data.messages.length - 1;
                        if (lastIndex < 0) {
                            dispatch({ type: 'GENERATING_STOPPED' });
                            return;
                        }

                        const lastUserMsgFromBackend = findLastUserMessage(data.messages);

                        const finalMessage: Message = {
                            ...currentStreamingMsg,
                            text: cleanText,
                            thinking: thinkingText,
                            timestamp: data.messages[lastIndex].timestamp,
                            documents: data.messages[lastIndex].documents,
                            citations: citations.length > 0 ? citations : undefined,
                            isLoading: false,
                            streamingText: undefined,
                        };

                        dispatch({
                            type: 'STREAM_COMPLETED',
                            payload: {
                                conversationId,
                                finalMessage,
                                answerVariants: data.answerVariants,
                                documents: data.documents,
                                userMessageDocuments: lastUserMsgFromBackend?.documents ?? [],
                            },
                        });

                        triggerConversationRefresh();
                    } catch (error) {
                        showSnackbar(parseError(error), 'error');
                        dispatch({ type: 'STREAM_ERROR' });
                    }
                }
            });
        }, [showSnackbar, triggerConversationRefresh]);

    const sendMessage = useCallback(
        async (text: string, files: File[] | null, searchInternet: boolean) => {
            try {
                const isNewConversation = !conversationIdRef.current;
                const conversationId = await ensureConversationExists();

                const userMessage = createDefaultUserMessage(text, files ?? []);
                const loadingMessage = createDefaultLoadingMessage();

                dispatch({
                    type: 'MESSAGE_SENT',
                    payload: { conversationId, userMessage, loadingMessage, searchInternet },
                });

                const params: StreamMessageParams = {
                    conversationId,
                    question: text,
                    files,
                    searchInternet,
                };

                startStreamForConversation(params, conversationId);

                if (isNewConversation) {
                    isNavigatingFromSendRef.current = true; // set before navigate as a flag for new convo
                    triggerConversationRefresh();
                    navigate(`/chat/${conversationId}`);
                }
            } catch (error) {
                showSnackbar(parseError(error), 'error');
                dispatch({ type: 'STREAM_ERROR' });
            }
        },
        [ensureConversationExists, triggerConversationRefresh, navigate, showSnackbar, startStreamForConversation],
    );

    const stopGeneration = useCallback(() => {
        const currentId = conversationIdRef.current;

        if (currentId) {
            streamManager.stopStream(currentId);

            conversationService
                .cancelGeneration(currentId)
                .catch(() => undefined);

            conversationService.fetchConversation(currentId)
                .then(data => {
                    // guard against quick clicking of retry convo
                    if (conversationIdRef.current !== currentId) return;

                    const lastUserMsgFromBackend = findLastUserMessage(data.messages)

                    if (lastUserMsgFromBackend?.documents?.length) {
                        dispatch({
                            type: 'USER_MESSAGE_DOCUMENTS_UPDATED',
                            payload: lastUserMsgFromBackend.documents,
                        });
                    }
                }).catch(() => undefined);
        }

        dispatch({ type: 'GENERATING_STOPPED' });
    }, []);

    const retryMessage = useCallback(async () => {
        const currentId = conversationIdRef.current;
        if (!currentId) return;

        const lastUserMsg = findLastUserMessage(state.messages);
        if (!lastUserMsg) return;

        const loadingMessage = createDefaultLoadingMessage();

        dispatch({
            type: 'RETRY_STARTED',
            payload: {
                conversationId: currentId,
                loadingMessage,
                searchInternet: state.searchInternet,
            },
        });

        const params: StreamMessageParams = {
            conversationId: currentId,
            question: lastUserMsg.text,
            files: null,
            searchInternet: state.searchInternet,
            retry: true,
        };

        startStreamForConversation(params, currentId);
    }, [state.searchInternet, state.messages, startStreamForConversation]);

    const retryInNewChat = useCallback(async () => {
        if (state.isGenerating) return;

        const lastUserMsg = findLastUserMessage(state.messages);
        if (!lastUserMsg) return;

        // restore setup for disk files attached to convo
        const docsWithIds = (lastUserMsg.documents ?? [])
            .filter(doc => doc.documentId > 0);

        const files = docsWithIds.length > 0
            ? await Promise.all(
                docsWithIds.map(doc => collectionService.getDocumentFile(doc.documentId, doc.documentName))
            )
            : null;

        const {
            selectedGroupIds,
            selectedDocIds,
            selectedWholeCollectionIds,
            selectedModelId,
            searchInternet,
        } = state;

        dispatch({ type: 'CONVERSATION_RESET' });
        conversationIdRef.current = null;

        // restore setup for data source files attached to convo
        dispatch({ type: 'COLLECTIONS_UPDATED', payload: selectedGroupIds });
        dispatch({ type: 'DOCUMENTS_UPDATED', payload: selectedDocIds });
        dispatch({ type: 'WHOLE_COLLECTION_UPDATED', payload: selectedWholeCollectionIds });

        if (selectedModelId !== null) {
            dispatch({ type: 'MODEL_SET', payload: selectedModelId });
        }

        dispatch({ type: 'SEARCH_INTERNET_SET', payload: searchInternet });

        navigate('/chat');
        await sendMessage(lastUserMsg.text, files, searchInternet);
    }, [sendMessage, navigate, state]);

    const activateVariant = useCallback(
        async (variantId: number) => {
            try {
                await conversationService.activateVariant(variantId);
                dispatch({ type: 'VARIANT_ACTIVATED', payload: { variantId } });
            } catch (error) {
                showSnackbar(parseError(error), 'error');
            }
        },
        [showSnackbar],
    );

    const exportToPdf = useCallback(async () => {
        const currentId = conversationIdRef.current;

        if (!currentId) return;

        try {
            const blob = await conversationService.exportToPdf(currentId);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', `conversation-${currentId}.pdf`);

            document.body.appendChild(link);

            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);

            showSnackbar(t('chat.exportConversationSuccess'), 'success');
        } catch (error) {
            showSnackbar(parseError(error), 'error');
        }
    }, [showSnackbar, t]);

    const setInitialModel = useCallback((modelId: number) => {
        if (!conversationIdRef.current) {
            dispatch({ type: 'MODEL_SET', payload: modelId });
        }
    }, []);

    /***
     *
     * Sets a COMPLETELY NEW set of groups (collections) and documents, always in partial mode (wholeCollectionIds is always reset to []).
     * IMPORTANT: this function does not "add" or "remove" anything on its own.
     * It accepts pre-calculated arrays and passes them on (dispatch + database write).
     * The CALLER (component / other context) is responsible for calculating the final content of groupIds/docIds - e.g., by filtering out a removed item from the old state.
     * USAGE: when removing an entire source (collection) from the list, you pass the old state minus the removed ID; the function treats the result as "the complete truth."
     *
     */
    const updateSourcesFromUser = useCallback(
        async (groupIds: number[], docIds: number[]) => {
            if (isLoadingFromBackendRef.current) return;

            dispatch({ type: 'COLLECTIONS_UPDATED', payload: groupIds });
            dispatch({ type: 'DOCUMENTS_UPDATED',   payload: docIds });
            dispatch({ type: 'WHOLE_COLLECTION_UPDATED', payload: [] });

            const currentId = conversationIdRef.current;
            if (!currentId) return;

            try {
                await conversationService.updateCollections(currentId, groupIds);
                await conversationService.updateDocuments(currentId, docIds);
            } catch (error) {
                showSnackbar(parseError(error), 'error');
            }
        },
        [showSnackbar],
    );

    /***
     *
     * Sets a COMPLETELY NEW set of collection (collectionIds). Collections that are new, compared to the previous state, automatically enter "whole" mode (entire collection).
     * Collections that were already in "whole" mode and remain in the new groupIds retain this mode.
     * Collections removed from collectionIds are also removed from wholeCollectionIds.
     * IMPORTANT: function itself does NOT decide whether this is an "addition" or a "replacement" from the user's perspective.
     * The CALLER decides this by passing:
     * - [...state.selectedWholeCollectionIds, newCollection] -> effect: addition to existing ones
     * - [newCollection] -> effect: replacement of everything
     * The function merely calculates the "whole" mode for NEW items in the provided array, relative to the previous state, and passes the result on (dispatch + database).
     * Usage: adding entire collections (without selecting individual documents), e.g., default collections, selection from a collection multi-select.
     *
     * */
    const updateCollectionsFromUser = useCallback(
        async (collectionIds: number[]) => {
            if (isLoadingFromBackendRef.current) return;

            const previousCollectionIds = new Set(state.selectedWholeCollectionIds);
            const newAddedIds = collectionIds.filter(id => !previousCollectionIds.has(id));

            // keep the whole mode for collections, which are still saved in selectedWholeCollectionIds state
            // new collections are saved in the whole collection mode by default

            const keptWholeIds = state.selectedWholeCollectionIds.filter(id => collectionIds.includes(id));
            const nextWholeCollectionIds = Array.from(new Set([...keptWholeIds, ...newAddedIds]));

            // in case it has to be shown in the FilePickerPanel
            // dispatch({ type: 'COLLECTIONS_UPDATED', payload: collectionIds });
            dispatch({ type: 'WHOLE_COLLECTION_UPDATED', payload: nextWholeCollectionIds});

            const currentId = conversationIdRef.current;
            if (!currentId) return;

            try {
                await conversationService.updateCollections(currentId, nextWholeCollectionIds);
                await conversationService.updateDocuments(
                    currentId,
                    buildConversationSelectionPayload(
                        [],
                        state.selectedDocIds,
                        nextWholeCollectionIds,
                    ).documentIds,
                );
            } catch (error) {
                showSnackbar(parseError(error), 'error');
            }
        },
        [showSnackbar, state.selectedDocIds, state.selectedWholeCollectionIds],
    );

    /***
     *
     * Sets a COMPLETELY NEW set of documents (docIds) and the "whole" mode for the collection
     * (wholeCollectionIds) - accepted directly as arguments, without any default calculation performed inside the function.
     * IMPORTANT: this function does not touch groupIds at all.
     * It assumes that the set of active collections has already been established (via updateCollectionsFromUser).
     * Like the other two, it does not "add" or "remove" anything itself:
     * the CALLER calculates the full, target array (e.g., by toggling a single document against the previous state) and passes it ready-made.
     * The function merely dispatches the action and sends it to the backend.
     * Usage: selecting/deselecting individual documents within already selected collections, toggling "whole" mode for a SPECIFIC, existing collection.
     *
     * */
    const updateDocumentsFromUser = useCallback(
        async (docIds: number[], wholeCollectionIds: number[]) => {
            if (isLoadingFromBackendRef.current) return;

            dispatch({ type: 'DOCUMENTS_UPDATED', payload: docIds });
            dispatch({ type: 'WHOLE_COLLECTION_UPDATED', payload: wholeCollectionIds });

            const currentId = conversationIdRef.current;
            if (!currentId) return;

            try {
                await conversationService.updateDocuments(
                    currentId,
                    buildConversationSelectionPayload(
                        state.selectedGroupIds,
                        docIds,
                        wholeCollectionIds,
                    ).documentIds,
                );
            } catch (error) {
                showSnackbar(parseError(error), 'error');
            }
        },
        [showSnackbar, state.selectedGroupIds],
    );

    const value = useMemo(
        () => ({
            state,
            autoScrollFinished,
            setAutoScrollFinished,
            sendMessage,
            retryMessage,
            retryInNewChat,
            stopGeneration,
            updateSourcesFromUser,
            updateCollectionsFromUser,
            updateDocumentsFromUser,
            loadConversationFromBackend,
            createNewConversation,
            activateVariant,
            exportToPdf,
            setInitialModel,
        }),
        [
            state,
            autoScrollFinished,
            setAutoScrollFinished,
            sendMessage,
            retryMessage,
            retryInNewChat,
            stopGeneration,
            updateSourcesFromUser,
            updateCollectionsFromUser,
            updateDocumentsFromUser,
            loadConversationFromBackend,
            createNewConversation,
            activateVariant,
            exportToPdf,
            setInitialModel,
        ],
    );

    return <ConversationContext.Provider value={value}>{children}</ConversationContext.Provider>;
};

// --- Hook ---

export const useConversation = () => {
    const context = useContext(ConversationContext);
    if (!context) {
        throw new Error('useConversation must be used within ConversationProvider');
    }
    return context;
};
